/**
 * The plan chooser — ARCHITECTURE §05.7, vendor attribution made concrete.
 *
 * **The client never decides what to suppress.** The API has already nulled
 * `brandName` and `partnerMno` under `vendorAttribution: SUPPRESS`, verified
 * across every occurrence in Phase 03. This component's only job is to **not
 * render an empty column**: a "Brand" header over a column of dashes is worse
 * than no column at all, because it advertises that something was hidden — and
 * the whole point of SUPPRESS is that the tenant's own customers cannot tell
 * whose network this is.
 *
 * Everything else is byte-identical between the two modes. `planName`,
 * `planCode`, `planDescription` and `price` reconcile against the invoice, so
 * masking one would make the panel disagree with the bill.
 */

import type { PlanSummary } from '@rd/shared';
import { useMemo, type ReactElement } from 'react';

import { Field } from '../../../components/Field.js';
import { Select } from '../../../components/Input.js';

export interface PlanPickerProps {
  plans: PlanSummary[];
  value: string | null;
  onChange(planCode: string): void;
  label?: string;
  error?: string | undefined;
}

/**
 * True when at least one plan carries attribution. Derived from the **data**,
 * not from a mode flag, so a tenant on SHOW whose upstream simply did not send
 * a brand also gets no empty column.
 */
export function hasAttribution(plans: PlanSummary[]): boolean {
  return plans.some((p) => Boolean(p.brandName) || Boolean(p.partnerMno));
}

export function PlanPicker({
  plans,
  value,
  onChange,
  label = 'Plan',
  error,
}: PlanPickerProps): ReactElement {
  const showBrand = useMemo(() => hasAttribution(plans), [plans]);

  return (
    <div className="flex flex-col gap-2">
      <Field label={label} required error={error}>
        {(fieldProps) => (
          <Select {...fieldProps} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
            {/* No ellipsis: '…' anywhere in a flow's DOM is the elision guard's
                trip-wire (activationFlow.spec — "elides none"), so decorative
                placeholder dots are banned, not just truncation. */}
            <option value="">Choose a plan</option>
            {plans.map((plan) => (
              <option key={plan.planCode} value={plan.planCode}>
                {planOptionLabel(plan, showBrand)}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {plans.length > 0 ? (
        <div className="overflow-x-auto rounded-[var(--brand-radius)] border border-line">
          <table className="w-full border-collapse text-xs">
            <caption className="sr-only">Available plans</caption>
            <thead className="bg-surface-alt">
              <tr>
                <th scope="col" className="px-2 py-1 text-left font-medium text-muted">
                  Plan
                </th>
                <th scope="col" className="px-2 py-1 text-left font-medium text-muted">
                  Code
                </th>
                {/* Absent under SUPPRESS — not present-and-empty (§05.7). */}
                {showBrand ? (
                  <th
                    scope="col"
                    data-testid="plan-brand-header"
                    className="px-2 py-1 text-left font-medium text-muted"
                  >
                    Brand
                  </th>
                ) : null}
                <th scope="col" className="px-2 py-1 text-right font-medium text-muted">
                  Price
                </th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.planCode} className="border-t border-line bg-surface">
                  <td className="px-2 py-1 text-body">{plan.planName ?? plan.planCode}</td>
                  <td className="px-2 py-1 font-mono text-body">{plan.planCode}</td>
                  {showBrand ? (
                    <td className="px-2 py-1 text-muted">{plan.brandName ?? plan.partnerMno}</td>
                  ) : null}
                  <td className="px-2 py-1 text-right tabular-nums text-body">
                    {plan.price ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

export function planOptionLabel(plan: PlanSummary, showBrand: boolean): string {
  const name = plan.planName ?? plan.planCode;
  const price = plan.price ? ` — ${plan.price}` : '';
  const brand = showBrand && plan.brandName ? ` (${plan.brandName})` : '';
  return `${name}${price}${brand}`;
}
